import { gql, useMutation, useQuery } from '@apollo/client'
import { ContentCopy } from '@mui/icons-material'
import { Button, DialogActions, DialogContent, Grid } from '@mui/material'
import {
  CreateShareFacebookShareChildAttachment,
  DigitalAsset,
  Mutation,
  MutationCreateShareArgs,
  Query,
  ShareSetting,
} from '@promoboxx/graphql-gateway-types'
import {
  BaseAdMedia,
  embedSocialPreview,
  SocialPreviewAdOptions,
} from '@promoboxx/social-preview'
import { Formik, FormikProps } from 'formik'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router'

import analytics from '@src/analytics'
import MobileFullScreenDialog from '@src/components/shared/MobileFullScreenDialog'
import {
  GET_SHARE_SETTING_QUERY,
  SHARESETTING_FIELDS,
} from '@src/components/shareDialog/queries'
import { invalidateApolloQuery } from '@src/config/ApolloClient'
import { useCustomFields } from '@src/customFields/customFieldsContext'
import cloudinaryRef from '@src/lib/Cloudinary'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import { useRetailerAccountSocialAccounts } from '@src/lib/useRetailerAccountSocialAccounts'

import ShareSettingForm, {
  ShareSettingDialogFormModel,
  ShareSettingDialogFormValues,
} from './ShareSettingForm'

const getSubmitText = (scheduledFor: string | undefined, shareType: string) => {
  const channelLabel = I18n.t(`channels.${shareType}`, {
    defaultValue: shareType,
  })
  const isScheduled = !!scheduledFor
  const isEmail = shareType === 'email'
  const submitTextKey = isEmail
    ? isScheduled
      ? 'schedule'
      : 'send'
    : isScheduled
      ? 'scheduleOn'
      : 'shareOn'
  return I18n.t(`shareDialog.submit.${submitTextKey}`, {
    channel: channelLabel,
  })
}
const ShareSettingDialog: React.FC = () => {
  // FB Carousel
  // const shareSettingId = 19767
  // Regular FB
  // const shareSettingId = 28873
  // Instagram Video
  // const shareSettingId = 28909
  // Regular Instagram
  // const shareSettingId = 28901
  // Regular Twitter
  // const shareSettingId = 28931
  // Twitter Video
  // const shareSettingId = 28934
  // Email Share
  // const shareSettingId = 28841
  // Embed Share
  // const shareSettingId = 28872
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()
  const location = useLocation()

  const { shareSettingId, brandId } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const shareSettingId = params.get('shareSettingId')
    const brandId = params.get('shareSettingBrandId')

    return {
      shareSettingId,
      brandId,
    }
  }, [location.search])

  const customFieldsContext = useCustomFields()
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()

  const retailerId = ensureCurrentRetailerAccount.retailers?.find(
    (x) => x.brand?.id === brandId,
  )?.id

  const { data, loading } = useQuery<Query>(GET_SHARE_SETTING_QUERY, {
    variables: {
      id: shareSettingId,
      retailer_id: retailerId,
      fields: SHARESETTING_FIELDS,
    },
    skip: !shareSettingId || !retailerId,
  })
  const [createShare] = useMutation<Mutation>(CREATE_SHARE)
  const formikRef = useRef<FormikProps<ShareSettingDialogFormValues> | null>(
    null,
  )
  const socialPreviewContainer = useRef<HTMLDivElement | null>(null)

  const shareSetting: ShareSetting | undefined = shareSettingId
    ? data?.getShareSettingRetailer
    : undefined

  const socialAccounts = useRetailerAccountSocialAccounts()

  // Update formik without using `enableReinitialize`.
  useEffect(() => {
    if (shareSetting) {
      const model: ShareSettingDialogFormModel = {
        message: '',
        link: shareSetting.retailer_link || '',
        share_url_type: shareSetting.retailer_share_url_type || '',
        allow_retailer_url_override:
          shareSetting.allow_retailer_url_override || false,
        facebookPageId: socialAccounts.defaultFacebookPageId,
        twitterAccountId: socialAccounts.defaultTwitterAccountId,
        instagramPageId: socialAccounts.defaultInstagramPageId,
        scheduled_for: '',
        selected_imports: [],
        subject: shareSetting.retailer_subject || '',
        child_attachments:
          shareSetting.share_data?.share_data?.rendered_child_attachments?.map(
            (x) => {
              return {
                id: x.id,
                url: x.url || '',
                // Intentionally using .url instead of .link here, as that has
                // the retailer URL applied.
                link: x.url || '',
                allow_retailer_url_override:
                  x.allow_retailer_url_override || false,
                share_url_type: x.share_url_type || '',
              }
            },
          ) || [],
      }

      formikRef.current?.setFieldValue('model', model, false)
      formikRef.current?.setFieldValue('carouselIndex', 0, false)
      formikRef.current?.setFieldValue('shareSetting', shareSetting, false)
      formikRef.current?.setFieldValue('schedulePreset', 'now', false)
    }
  }, [
    shareSetting,
    socialAccounts.defaultFacebookPageId,
    socialAccounts.defaultTwitterAccountId,
    socialAccounts.defaultInstagramPageId,
  ])

  function handleClose() {
    const params = new URLSearchParams(window.location.search)
    params.delete('shareSettingId')

    history.push(`${window.location.pathname}?${params.toString()}`)

    formikRef.current?.setErrors({})
    formikRef.current?.setTouched({})
  }

  const retailer = useMemo(() => {
    return ensureCurrentRetailerAccount.retailers?.find(
      (x) => x.brand?.id === data?.getShareSettingRetailer?.campaign?.brand?.id,
    )
  }, [
    data?.getShareSettingRetailer?.campaign?.brand?.id,
    ensureCurrentRetailerAccount.retailers,
  ])

  return (
    <Formik<ShareSettingDialogFormValues>
      onSubmit={async (values, formikActions) => {
        if (!shareSettingId) {
          console.error('No shareSettingId')
          return
        }

        formikActions.setSubmitting(true)

        const share: MutationCreateShareArgs = {
          resource_type: 'retailers',
          resource_id: retailer?.id || '',
          shareType: shareSetting?.share_type || '',
        }

        if (shareSetting?.share_type === 'facebook') {
          share.facebook_share = {
            retailer_id: retailer?.id || '',
            share_setting_id: shareSettingId,
            scheduled_for: values.model.scheduled_for || undefined,
            message: values.model.message,
            facebook_page_id: values.model.facebookPageId,
          }

          if (shareSetting.child_attachments?.length) {
            share.facebook_share.child_attachments =
              values.model.child_attachments.map((childAttachment) => {
                const ret: CreateShareFacebookShareChildAttachment = {
                  id: childAttachment.id,
                  description: childAttachment.description,
                  name: childAttachment.name,
                  retailer_url_override: childAttachment.link,
                }

                if (childAttachment.allow_retailer_url_override) {
                  ret.retailer_url_override = childAttachment.link
                }

                return ret
              })
          }
        } else if (shareSetting?.share_type === 'instagram') {
          share.instagram_share = {
            share_setting_id: shareSettingId,
            message: values.model.message,
            share_mode: 'manual',
            facebook_page_id: values.model.instagramPageId,
            scheduled_for: values.model.scheduled_for || undefined,
          }
        } else if (shareSetting?.share_type === 'twitter') {
          share.twitter_share = {
            retailer_id: retailer?.id || '',
            share_setting_id: shareSettingId,
            text: values.model.message,
            twitter_account_id: values.model.twitterAccountId,
            link: values.model.link || undefined,
            scheduled_for: values.model.scheduled_for || undefined,
          }
        } else if (shareSetting?.share_type === 'email') {
          share.email_share = {
            subject: values.model.subject,
            text_body: shareSetting.retailer_body || '',
            html_body: shareSetting.retailer_body || '',
            scheduled_for: values.model.scheduled_for || undefined,
            selected_campaigns: data?.getShareSettingRetailer?.campaign?.id
              ? [data.getShareSettingRetailer.campaign.id]
              : [],
            selected_imports: values.model.selected_imports,
            share_setting_id: shareSettingId,
          }
        }

        const eventName = `${
          values.model.scheduled_for ? 'schedule' : 'create'
        }_${shareSetting?.share_type}_share`
        const commonEventData = {
          scheduled: values.model.scheduled_for,
          success: false,
          share_id: null,
          share_setting_id: shareSettingId,
          brand_id: retailer?.brand?.id,
          retailer_id: retailer?.id,
          campaign_id: shareSetting?.campaign?.id,
          channel: shareSetting?.share_type,
          url: window.location.href,
        }

        try {
          const response = await createShare({ variables: share })
          analytics.track({
            event: 'Action',
            type: eventName,
            data: {
              ...commonEventData,
              success: true,
              share_id: response.data?.createShare?.id,
            },
          })

          values.model.scheduled_for
            ? enqueueSnackbar(
                I18n.t('shareDialog.notifications.createShecduledShareSuccess'),
                {
                  variant: 'success',
                },
              )
            : enqueueSnackbar(
                I18n.t('shareDialog.notifications.createShareSuccess'),
                {
                  variant: 'success',
                },
              )
          invalidateApolloQuery(['getShares'])
          handleClose()
        } catch (err) {
          analytics.track({
            event: 'Action',
            type: eventName,
            data: commonEventData,
          })

          console.error(err)
          enqueueSnackbar(
            I18n.t('shareDialog.notifications.createShareError'),
            {
              variant: 'error',
            },
          )
        }

        formikActions.setSubmitting(false)
      }}
      initialValues={{
        model: {
          share_url_type: '',
          allow_retailer_url_override: false,
          message: '',
          link: '',
          facebookPageId: '-1',
          twitterAccountId: '-1',
          instagramPageId: '-1',
          child_attachments: [],
          scheduled_for: '',
          selected_imports: [],
          subject: '',
        },
        shareSetting: undefined,
        carouselIndex: 0,
        schedulePreset: '',
      }}
    >
      {(formikProps) => {
        formikRef.current = formikProps

        function updateSocialPreview() {
          if (!shareSetting) {
            return
          }

          const digitalAssetLookup: Record<string, DigitalAsset> = {}
          for (const asset of shareSetting?.digital_asset_associables || []) {
            if (!asset.id) {
              continue
            }

            digitalAssetLookup[asset.id] = asset
          }

          let type: SocialPreviewAdOptions['type'] = 'facebook/single'

          if (shareSetting?.share_type === 'instagram') {
            type = 'instagram/organic'
          } else if (shareSetting?.share_type === 'twitter') {
            type = 'twitter'
          } else if (shareSetting?.share_type === 'email') {
            type = 'email'
          } else if (shareSetting?.share_type === 'embed') {
            type = 'embed'
          }

          const facebookPage = socialAccounts.facebookPages.find(
            (x) => x.id === formikProps.values.model.facebookPageId,
          )
          const twitterAccount = socialAccounts.twitterAccounts.find(
            (x) => x.id === formikProps.values.model.twitterAccountId,
          )
          const instagramPage = socialAccounts.instagramPages.find(
            (x) => x.id === formikProps.values.model.instagramPageId,
          )
          const emailPaylodProps = {
            emailType:
              shareSetting.share_type === 'email' ? 'email' : undefined,
            subject:
              type === 'email' ? formikProps.values.model.subject : undefined,
          }

          const facebookPayloadProps = {
            shouldShowLink:
              type === 'facebook/single'
                ? shareSetting?.share_url_type &&
                  shareSetting?.post_format !== 'video'
                  ? true
                  : false
                : undefined,
          }

          // handle facebook preview share link
          let shareLink = ''
          if (shareSetting?.link && type === 'facebook/single') {
            if (shareSetting?.share_url_type === 'landing_page') {
              shareLink =
                shareSetting.retailer_link ||
                `${shareSetting.link}?share_setting_id=${shareSetting?.id}`
            } else {
              shareLink = shareSetting.link
            }
          }

          const socialPreviewOptions: SocialPreviewAdOptions = {
            type,
            payload: {
              ...emailPaylodProps,
              ...facebookPayloadProps,
              carouselIndex: formikProps.values.carouselIndex,
              // Empty space since the Twitter preview includes its own
              // placeholder message for some reason.
              primaryText: formikProps.values.model.message || ' ',
              userFullName:
                shareSetting.share_type === 'facebook'
                  ? facebookPage?.name || undefined
                  : shareSetting.share_type === 'twitter'
                    ? twitterAccount?.name || undefined
                    : shareSetting.share_type === 'instagram'
                      ? instagramPage?.name || undefined
                      : undefined,
              userHandle:
                shareSetting.share_type === 'facebook'
                  ? facebookPage?.name || undefined
                  : shareSetting.share_type === 'twitter'
                    ? twitterAccount?.nickname || undefined
                    : shareSetting.share_type === 'instagram'
                      ? instagramPage?.name || undefined
                      : undefined,
              userAvatar:
                shareSetting.share_type === 'facebook'
                  ? facebookPage?.picture_url || undefined
                  : shareSetting.share_type === 'twitter'
                    ? twitterAccount?.profile_image_url || undefined
                    : shareSetting.share_type === 'instagram'
                      ? instagramPage?.picture_url || undefined
                      : undefined,
              media:
                shareSetting?.post_format === 'carousel'
                  ? shareSetting?.child_attachments?.map((x) => {
                      const baseAdMedia: BaseAdMedia = {
                        headline: x.title || undefined,
                        description: x.description || undefined,
                        url: x.link || undefined,
                        callToAction: x.call_to_action || undefined,
                        ...convertDigitalAssetToBaseAdMedia(
                          digitalAssetLookup[x.media?.source_id || ''],
                        ),
                      }

                      return baseAdMedia
                    }) || []
                  : [
                      {
                        headline:
                          type === 'facebook/single' &&
                          shareSetting?.retailer_title
                            ? shareSetting.retailer_title
                            : shareSetting?.title || undefined,
                        description:
                          type === 'facebook/single' &&
                          shareSetting?.retailer_description
                            ? shareSetting.retailer_description
                            : shareSetting?.description || undefined,
                        callToAction:
                          shareSetting.share_type === 'email'
                            ? shareSetting?.share_data?.share_data
                                ?.button_text || ''
                            : undefined,
                        ...convertDigitalAssetToBaseAdMedia(
                          shareSetting?.digital_asset_associables?.[0],
                          shareSetting?.share_type === 'email' ? 350 : 800,
                        ),
                        url: shareLink,
                      },
                    ],
            },
            events: {
              'socialPreview/carouselIndexUpdated': (event) => {
                formikProps.setFieldValue(
                  'carouselIndex',
                  // TODO some previews send `index` instead of
                  // `carouselIndex`.
                  // https://github.com/search?q=repo%3Apromoboxx%2Fsocial-preview%20socialPreview%2FcarouselIndexUpdated&type=code
                  event.data.payload.carouselIndex ?? event.data.payload.index,
                )
              },
            },
          }

          if (socialPreviewContainer.current) {
            embedSocialPreview(
              socialPreviewContainer.current,
              socialPreviewOptions,
            )
          }
        }

        updateSocialPreview()

        return (
          <MobileFullScreenDialog
            open={!!shareSettingId}
            onClose={() => {
              handleClose()
            }}
            fullWidth
            maxWidth="md"
          >
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} order={{ xs: 2, sm: 1 }}>
                  <div
                    ref={socialPreviewContainer}
                    style={{ position: 'sticky', top: 0, left: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} order={{ xs: 1, sm: 2 }}>
                  <ShareSettingForm
                    loading={
                      loading ||
                      socialAccounts.loading ||
                      customFieldsContext.loading
                    }
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  handleClose()
                }}
              >
                {I18n.t('shared.close')}
              </Button>

              {shareSetting?.share_type === 'embed' ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ContentCopy />}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        shareSetting?.share_data?.share_data?.embed_code || '',
                      )
                      enqueueSnackbar(
                        I18n.t('shareDialog.embeds.notifications.success'),
                        {
                          variant: 'success',
                        },
                      )
                      handleClose()
                    } catch (err) {
                      console.error(err)
                      enqueueSnackbar(
                        I18n.t('shareDialog.embeds.notifications.error'),
                        {
                          variant: 'error',
                        },
                      )
                    }
                  }}
                >
                  {I18n.t('shareDialog.embeds.submit')}
                </Button>
              ) : (
                <Button
                  disabled={
                    formikProps.isSubmitting ||
                    formikProps.isValidating ||
                    !formikProps.isValid
                  }
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    formikProps.submitForm()
                  }}
                >
                  {getSubmitText(
                    formikProps.values.model.scheduled_for,
                    shareSetting?.share_type || '',
                  )}
                </Button>
              )}
            </DialogActions>
          </MobileFullScreenDialog>
        )
      }}
    </Formik>
  )
}

const CREATE_SHARE = gql`
  mutation createShare(
    $shareType: String!
    $resource_id: ID!
    $asset_share: CreateShareAssetShare
    $email_share: CreateShareEmailShare
    $facebook_share: CreateShareFacebookShare
    $twitter_share: CreateShareTwitterShare
    $instagram_share: CreateShareInstagramShare
  ) {
    createShare(
      shareType: $shareType
      resource_type: "retailers"
      resource_id: $resource_id
      asset_share: $asset_share
      email_share: $email_share
      facebook_share: $facebook_share
      twitter_share: $twitter_share
      instagram_share: $instagram_share
    ) {
      id
      status
    }
  }
`

export function convertDigitalAssetToBaseAdMedia(
  digitalAsset: DigitalAsset | undefined | null,
  width: number | undefined = 800,
) {
  const ret: Pick<BaseAdMedia, 'mediaType' | 'mediaSrc'> = {
    mediaType: 'image',
    mediaSrc: '',
  }

  if (!digitalAsset) {
    return ret
  }

  if (digitalAsset.external_video_url) {
    ret.mediaType = 'video'
    ret.mediaSrc = digitalAsset.external_video_url
    return ret
  }

  const mediaType: BaseAdMedia['mediaType'] =
    digitalAsset.file?.resource_type === 'video' ? 'video' : 'image'

  const publicId = digitalAsset.file?.public_id

  if (!publicId) {
    return ret
  }

  const mediaSrc =
    mediaType === 'image'
      ? cloudinaryRef.getCloudinaryUrl(publicId, {
          transformation: [
            {
              format: 'jpg',
              crop: 'crop',
              width: digitalAsset.crop_width || undefined,
              height: digitalAsset.crop_height || undefined,
              x: digitalAsset.crop_origin_x || undefined,
              y: digitalAsset.crop_origin_y || undefined,
            },
            { crop: 'auto', width },
          ],
        })
      : cloudinaryRef.getCloudinaryVideoUrl(publicId)

  ret.mediaType = mediaType
  ret.mediaSrc = mediaSrc

  return ret
}

export default ShareSettingDialog
